/**
 * Α4: **ο τόνος μιας ετικέτας, παραγόμενος από το κείμενό της.**
 *
 * Η ίδια ετικέτα πρέπει να έχει **πάντοτε** το ίδιο χρώμα — σε κάθε κάρτα, σε
 * κάθε οθόνη, σε κάθε συνεδρία. Χωρίς αυτό το χρώμα γίνεται διακόσμηση: ο
 * χρήστης δεν μπορεί να μάθει ότι «το πράσινο είναι η προθεσμία», και μια
 * πληροφορία που αλλάζει σε κάθε φόρτωση είναι θόρυβος.
 *
 * Γι' αυτό **δεν** υπάρχει μετρητής θέσης, ούτε τυχαιότητα, ούτε αποθηκευμένο
 * πεδίο: το χρώμα είναι **συνάρτηση του κειμένου** και μόνο. Πόρος καταχωρημένος
 * σήμερα και πόρος καταχωρημένος πέρσι δείχνουν την ετικέτα «horizon» με το ίδιο
 * χρώμα, χωρίς τίποτα να έχει αποθηκευτεί.
 *
 * ⚠ **ΚΑΜΙΑ ΝΕΑ ΠΑΛΕΤΑ.** Οι τόνοι είναι **οι υπάρχοντες** του `StatusChip`,
 * που έχουν ήδη ελεγμένη αντίθεση από το `design/build-tokens.mjs`. Παραγωγή
 * χρώματος με αριθμητική πάνω σε απόχρωση —το προφανές `hsl(hash % 360)`— θα
 * παρήγαγε ζεύγη που **κανείς δεν έχει μετρήσει**, δηλαδή θα έβγαζε τη
 * ΜΛΑ-11 από τον έλεγχο του ενός αρχείου.
 */

/**
 * Οι τόνοι που δέχεται το `StatusChip`, στη σειρά που τους θέλουμε σταθερή.
 *
 * Το `danger` λείπει **σκόπιμα**: κόκκινη ετικέτα διαβάζεται ως προειδοποίηση,
 * και οι ετικέτες δεν είναι καταστάσεις. Οι τέσσερις αρκούν — ο στόχος δεν είναι
 * μοναδικό χρώμα ανά ετικέτα (αδύνατο με σταθερή παλέτα) αλλά **σταθερή
 * ομαδοποίηση**: δύο κάρτες με την ίδια ετικέτα ταιριάζουν με τη ματιά.
 */
export const TAG_TONES = Object.freeze(['purple', 'teal', 'warning', 'neutral']);

/**
 * Κανονικοποίηση **πριν** από τον κατακερματισμό.
 *
 * Χωρίς αυτό, «Horizon» και «horizon» παίρνουν διαφορετικό χρώμα ενώ ο χρήστης
 * τα διαβάζει ως την ίδια ετικέτα. Δεν αγγίζει τους **τόνους**: εκεί η
 * εξομάλυνση θα ήταν λάθος, γιατί «ερευνά» και «έρευνα» είναι άλλες λέξεις —
 * είναι ακριβώς η διάκριση που κόστισε στην αναζήτηση έναντι των αρχικών ονόματος.
 */
export const normalizeTag = (tag) => String(tag ?? '').trim().toLowerCase();

/**
 * Κατακερματισμός FNV-1a, 32 bits.
 *
 * Επιλέχθηκε γιατί είναι **λίγες γραμμές και ντετερμινιστικός σε κάθε μηχανή**:
 * καμία εξάρτηση, καμία διαφορά ανάμεσα σε περιηγητές. Η ποιότητα διασποράς
 * αρκεί για τέσσερις κάδους — δεν κρύβει τίποτα και δεν χρειάζεται να κρύψει.
 */
function hash32(text) {
    let value = 0x811c9dc5;

    for (let index = 0; index < text.length; index += 1) {
        value ^= text.charCodeAt(index);
        // Πολλαπλασιασμός με 16777619, γραμμένος με ολισθήσεις ώστε να μείνει
        // μέσα στα 32 bits του JavaScript χωρίς απώλεια ακρίβειας.
        value = (value + ((value << 1) + (value << 4) + (value << 7) + (value << 8) + (value << 24))) >>> 0;
    }

    return value >>> 0;
}

/** Ο τόνος του `StatusChip` για μία ετικέτα. Κενή ετικέτα → ουδέτερος. */
export function toneOfTag(tag) {
    const text = normalizeTag(tag);
    if (text === '') return 'neutral';

    return TAG_TONES[hash32(text) % TAG_TONES.length];
}

export default toneOfTag;
